<script>
  import {
    listSamples, getPref, setPref, putSample, applyTarget, startRecording,
    recorderSupported, recorderIssue, liveSamples, activeSample, canRecord, nextSlot, MAX_SAMPLES,
  } from './recordings.js'
  import { trimmedWavFromBlob } from './audio.js'

  // One recordable sound (a phoneme or the whole word). Record up to 3 takes,
  // pick the active one, soft-delete/restore, and switch recorded vs standard.
  let { target, reloadKey = 0, phonicKey = null } = $props()

  let samples = $state([])
  let pref = $state({ mode: 'recorded', activeSlot: 0 })
  let usePhonic = $state(readPhonic())
  function readPhonic() { try { return !!phonicKey && localStorage.getItem(phonicKey) === '1' } catch (_) { return false } }
  function setPhonic(v) { usePhonic = v; try { localStorage.setItem(phonicKey, v ? '1' : '0') } catch (_) {} }
  let recording = $state(false)
  let counting = $state(0) // 3,2,1 countdown before capture; 0 = not counting
  let elapsed = $state(0)
  let status = $state('')
  let busy = $state(false)
  let controller = null
  let tick = null
  let acquiring = false // true while awaiting getUserMedia — blocks a duplicate mic acquire
  let destroyed = false // component torn down / reloaded while acquiring

  const supported = recorderSupported()
  const issue = recorderIssue()

  $effect(() => {
    reloadKey // reload when the parent bumps it (e.g. after Save)
    destroyed = false
    load()
    return () => {
      destroyed = true
      if (tick) { clearInterval(tick); tick = null }
      if (controller) { controller.cancel(); controller = null }
      recording = false; counting = 0; elapsed = 0
    }
  })

  async function load() {
    const [s, p] = await Promise.all([listSamples(target.key), getPref(target.key)])
    samples = s; pref = p
  }
  const live = () => liveSamples(samples)
  const isActive = (s) => { const a = activeSample(samples, pref); return !!a && a.slot === s.slot }
  const usingRecorded = () => pref.mode !== 'standard' && !!activeSample(samples, pref)

  async function toggleRecord() {
    if (busy || acquiring) return
    if (recording) return stop()
    if (counting) { cancelCountdown(); return } // tap during countdown cancels
    if (!canRecord(samples)) { status = `Keep up to ${MAX_SAMPLES} takes — delete one first.`; return }
    acquiring = true
    let ctrl = null
    try {
      status = ''
      ctrl = await startRecording() // mic permission prompt happens now
    } catch (e) {
      status = e && e.name === 'NotAllowedError' ? 'Microphone blocked — allow mic access in the browser.'
        : e && e.name === 'NotFoundError' ? 'No microphone found on this device.'
        : (recorderIssue() || 'Could not start recording.')
      return
    } finally {
      acquiring = false
    }
    if (destroyed) { ctrl.cancel(); return } // panel closed while acquiring → release mic
    controller = ctrl
    startCountdown()
  }
  function startCountdown() {
    counting = 3
    tick = setInterval(() => {
      counting -= 1
      if (counting <= 0) { clearInterval(tick); tick = null; counting = 0; beginRecording() }
    }, 1000)
  }
  function beginRecording() {
    if (!controller) return
    controller.begin()
    recording = true; elapsed = 0
    tick = setInterval(() => { elapsed += 1 }, 1000)
  }
  function cancelCountdown() {
    if (tick) { clearInterval(tick); tick = null }
    counting = 0
    if (controller) { controller.cancel(); controller = null }
  }
  async function stop() {
    if (!controller) return
    busy = true
    if (tick) { clearInterval(tick); tick = null }
    const raw = await controller.stop(); const rawMime = controller.mime
    controller = null; recording = false
    if (raw && raw.size) {
      const trimmed = await trimmedWavFromBlob(raw)   // cut leading/trailing silence
      const blob = trimmed ? trimmed.blob : raw
      const mime = trimmed ? trimmed.mime : rawMime
      const slot = nextSlot(samples)
      await putSample({ targetKey: target.key, slot, blob, mime, uploaded: false, deleted: false, ts: Date.now() })
      await setPref(target.key, { activeSlot: slot, mode: 'recorded' }) // newest take becomes the active one
      await applyTarget(target.key)
      await load()
    }
    busy = false
  }
  async function softDelete(s) {
    if (busy || recording || counting) return
    await putSample({ ...s, deleted: true })
    await applyTarget(target.key); await load()
  }
  async function chooseActive(s) {
    if (busy || recording || counting) return
    await setPref(target.key, { activeSlot: s.slot, mode: 'recorded' })
    await applyTarget(target.key); await load()
  }
  async function setMode(mode) {
    if (busy || recording || counting) return
    await setPref(target.key, { mode })
    await applyTarget(target.key); await load()
  }
  function playBlob(s) {
    const u = URL.createObjectURL(s.blob)
    let done = false
    const cleanup = () => { if (done) return; done = true; URL.revokeObjectURL(u) }
    const a = new Audio(u)
    a.onended = cleanup; a.onerror = cleanup
    a.play().catch(cleanup)
  }
</script>

<div class="target">
  <div class="head">
    <div class="label">
      <span class="txt">{target.label}</span>
      {#if target.sub}<span class="sub">{target.sub}</span>{/if}
    </div>
    {#if phonicKey}
      <div class="mode">
        <button class:sel={usePhonic} onclick={() => setPhonic(true)}>Phonic Sound</button>
        <button class:sel={!usePhonic} onclick={() => setPhonic(false)}>Recorded</button>
      </div>
    {:else if live().length}
      <div class="mode">
        <button class:sel={usingRecorded()} onclick={() => setMode('recorded')} disabled={busy || !!counting}>Recorded</button>
        <button class:sel={!usingRecorded()} onclick={() => setMode('standard')} disabled={busy || !!counting}>Standard</button>
      </div>
    {/if}
  </div>

  <div class="takes">
    {#each live() as s (s.id)}
      <div class="take" class:active={isActive(s)}>
        <button class="mini play" onclick={() => playBlob(s)} aria-label="Play take">▶</button>
        <button class="mini star" class:on={isActive(s)} onclick={() => chooseActive(s)} disabled={busy || !!counting} aria-label="Use this take">{isActive(s) ? '★' : '☆'}</button>
        <span class="dot" class:up={s.uploaded} title={s.uploaded ? 'saved to server' : 'on device'}></span>
        <button class="mini del" onclick={() => softDelete(s)} disabled={busy || !!counting} aria-label="Delete take">🗑</button>
      </div>
    {/each}
    {#if supported}
      <button class="rec" class:on={recording} class:count={counting} onclick={toggleRecord} disabled={busy || (!recording && !counting && !canRecord(samples))}>
        {recording ? `⏹ ${elapsed}s` : counting ? `${counting}…` : '● Rec'}
      </button>
    {/if}
  </div>

  {#if !supported}<p class="status">🎙 {issue}</p>{/if}
  {#if status}<p class="status">{status}</p>{/if}
</div>

<style>
  .target { background: #f4efd8; border-radius: 12px; padding: 1.2vh 1.4vh; display: flex; flex-direction: column; gap: 0.9vh; }
  .head { display: flex; align-items: center; justify-content: space-between; gap: 1vh; }
  .label { display: flex; align-items: baseline; gap: 0.8vh; min-width: 0; }
  .txt { font-weight: 800; color: #2e5d1f; font-size: 2.1vh; }
  .sub { color: #4a7a34; font-weight: 700; font-size: 1.7vh; }
  .mode { display: inline-flex; border-radius: 999px; overflow: hidden; border: 2px solid #2e5d1f; flex: none; }
  .mode button {
    border: none; background: #fffef0; color: #2e5d1f; font-family: inherit; font-weight: 800;
    font-size: 1.5vh; padding: 0.5vh 1.1vh; cursor: pointer;
  }
  .mode button.sel { background: #2e5d1f; color: #fffef0; }
  .takes { display: flex; align-items: center; gap: 0.9vh; flex-wrap: wrap; }
  .take { display: inline-flex; align-items: center; gap: 0.4vh; background: #fffef0; border-radius: 999px; padding: 0.3vh 0.6vh; }
  .take.active { box-shadow: 0 0 0 2px #ffd24d; }
  .mini {
    border: none; border-radius: 999px; cursor: pointer; font-size: 1.8vh;
    width: 3.8vh; height: 3.8vh; display: flex; align-items: center; justify-content: center; background: #eee;
  }
  .mini.play { background: #ffd24d; color: #7a4a00; }
  .mini.star { background: transparent; font-size: 2.1vh; }
  .mini.star.on { color: #e8a400; }
  .mini.del { background: #ffdad6; }
  .dot { width: 1vh; height: 1vh; border-radius: 50%; background: #c9b64e; }
  .dot.up { background: #3aa655; }
  .rec {
    border: none; border-radius: 999px; cursor: pointer; font-family: inherit; font-weight: 800;
    font-size: 1.7vh; padding: 0.6vh 1.4vh; background: #ff5a4d; color: #fff; box-shadow: 0 3px 0 rgba(0,0,0,0.18);
  }
  .rec.on { background: #b3271b; animation: pulse 1s ease-in-out infinite; }
  .rec.count { background: #e8a400; font-size: 2.2vh; min-width: 6vh; }
  .rec:disabled { opacity: 0.4; cursor: default; box-shadow: 0 1px 0 rgba(0,0,0,0.12); }
  .status { margin: 0; color: #b23; font-weight: 700; font-size: 1.6vh; }
  @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
</style>
